import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { ArrowLeft } from "lucide-react";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

export default function NewsArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { api.get(`/news/${slug}`).then((r) => setArticle(r.data)).catch(() => setMissing(true)); }, [slug]);

  if (missing) return <div className="py-32 text-center text-[var(--ink-2)]" data-testid="news-missing">Article not found. <Link to="/news" className="text-[var(--accent)] font-semibold">Back to news</Link></div>;
  if (!article) return <div className="py-32 text-center text-[var(--ink-2)]">Loading…</div>;

  return (
    <article className="mx-auto max-w-3xl px-4 sm:px-6 py-12" data-testid="news-article-page">
      <Seo title={article.title} description={article.excerpt} path={`/news/${article.slug}`} image={article.cover_url} />
      <Link to="/news" className="flex w-fit items-center gap-1.5 text-sm text-[var(--ink-2)] hover:text-[var(--ink)] mb-6"><ArrowLeft size={15} /> Back to news</Link>
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--accent)]">{fmtDate(article.published_at)}</span>
      <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter mt-2 mb-3 leading-[1.05]" data-testid="news-article-title">{article.title}</h1>
      {article.author && <p className="text-sm text-[var(--ink-2)] mb-6">By {article.author}</p>}
      {article.cover_video
        ? <video src={article.cover_video} controls playsInline className="w-full aspect-video object-cover border border-[var(--line)] bg-black mb-8" data-testid="news-cover-video" />
        : article.cover_url && <img src={article.cover_url} alt={article.title} className="w-full aspect-video object-cover border border-[var(--line)] mb-8" />}
      <ArticleBody body={article.body} inlineImage={article.inline_image_url} />
    </article>
  );
}

const URL_RE = /(https?:\/\/[^\s]+)/g;
function linkify(text) {
  return text.split(URL_RE).map((part, i) =>
    /^https?:\/\//.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-[var(--accent)] font-semibold underline break-words hover:opacity-80">{part}</a>
      : part
  );
}

function ArticleBody({ body, inlineImage }) {
  const text = body || "";
  const imgs = (inlineImage || "").split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
  const paras = text.split(/\n\s*\n/);
  if (!imgs.length) {
    return <div className="text-[var(--ink)]/85 leading-relaxed text-lg space-y-5">{paras.map((p, i) => <p key={i} className="whitespace-pre-line">{linkify(p)}</p>)}</div>;
  }
  const step = Math.max(1, Math.floor(paras.length / (imgs.length + 1)));
  const out = [];
  let imgIdx = 0;
  paras.forEach((p, i) => {
    out.push(<p key={`p${i}`} className="whitespace-pre-line mb-5">{linkify(p)}</p>);
    if (imgIdx < imgs.length && (i + 1) === step * (imgIdx + 1) && i + 1 < paras.length) {
      out.push(<figure key={`img${imgIdx}`} className="my-8"><img src={imgs[imgIdx]} alt="" className="w-full max-w-xl mx-auto border border-[var(--line)]" data-testid="news-inline-image" /></figure>);
      imgIdx++;
    }
  });
  while (imgIdx < imgs.length) {
    out.push(<figure key={`img${imgIdx}`} className="my-8"><img src={imgs[imgIdx]} alt="" className="w-full max-w-xl mx-auto border border-[var(--line)]" data-testid="news-inline-image" /></figure>);
    imgIdx++;
  }
  return <div className="text-[var(--ink)]/85 leading-relaxed text-lg">{out}</div>;
}
